import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSessionConfig, startSession, getSessions } from '../../services/api';
import LanternMark from '../../components/LanternMark';
import InstallPrompt from '../../components/InstallPrompt';

const C = {
  bg: '#FFFDF7',
  text: '#1A1F36',
  textMuted: '#5A6478',
  accent: '#F59E0B',
  accentDark: '#D97706',
  accentSoft: '#FEF3C7',
  border: '#F0E7D5',
  card: '#FFFFFF',
  green: '#10B981',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenInk: '#065F46',
  warnBg: '#FEF9C3',
  warnText: '#854D0E',
  warnBorder: '#FDE68A',
  errorBg: '#FEF2F2',
  errorText: '#B42318',
  errorBorder: '#FECACA',
};

const HEADING_FONT = "'Nunito', sans-serif";

const CODE_DESCRIPTION =
  'Get help writing and understanding code — Lantern guides you step by step. ' +
  'Write your code in Scratch, Python, or wherever you work, and bring it here for help.';

// Local copy of the same mapping in parent/ChildSessions.jsx — kept in sync
// deliberately. Lifting it to a shared module is on the consolidation backlog.
const MODE_LABEL = {
  homework:    'Homework',
  explore:     'Explore',
  revision:    'Revision',
  code:        'Code',
  eleven_plus: '11+ Prep',
};

// 'general' is the server-side sentinel for explore/code without a chosen
// subject. Surface it to children with friendly copy rather than the raw key.
function getSubjectMeta(subjectKey, configSubjects) {
  if (subjectKey === 'general' || !subjectKey) {
    return { displayName: 'Anything', icon: '✨' };
  }
  const match = configSubjects?.find((s) => s.key === subjectKey);
  return {
    displayName: match?.displayName || subjectKey,
    icon:        match?.icon        || '📖',
  };
}

// Friendly relative time. Falls back to a date string for anything older than
// a week so we don't surface "47 days ago" — at that point a date is more
// readable. UTC handling isn't critical here; the child's clock is fine.
function formatRelativeTime(ts) {
  if (!ts) return '';
  const now = Date.now();
  const diffMs = Math.max(0, now - ts);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1)  return 'Just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  // Day comparison uses local calendar days rather than 24h chunks so a session
  // started yesterday evening doesn't read as "23 hours ago" once it tips into
  // the next morning.
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const startOfThen  = new Date(ts); startOfThen.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfToday - startOfThen) / 86400000);
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7)   return `${dayDiff} days ago`;

  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function buildSubjectModes(effectiveYear, elevenPlusAvailable) {
  const isPrimary = effectiveYear <= 6;
  const codeLabel = isPrimary
    ? 'Code — think like a programmer'
    : 'Code — write and learn real code';
  const modes = [
    { key: 'homework', label: 'Homework', icon: '📚', description: 'Work through an assignment with guidance' },
    { key: 'explore',  label: 'Explore',  icon: '🔭', description: 'Ask questions and learn freely' },
    { key: 'revision', label: 'Revision', icon: '🧠', description: 'Review and remember what you have learned' },
    { key: 'code',     label: codeLabel,  icon: '💻', description: CODE_DESCRIPTION },
  ];
  // 11+ tile — only when the server has flagged the child as eligible
  // (Year 4–6 + Plus / Family / beta tier). Server-side gate in
  // /session/start re-validates so a tampered client cannot bypass.
  if (elevenPlusAvailable) {
    modes.push({
      key: 'eleven_plus',
      label: '11+ Preparation',
      icon: '🎯',
      description: 'GL Assessment practice — Verbal Reasoning and Maths',
    });
  }
  return modes;
}

export default function SubjectSelector() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState(null);
  const [mode, setMode] = useState('homework');
  const [starting, setStarting] = useState(false);
  const [outsideHours, setOutsideHours] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);
  // Code mode hides the subject grid by default; child opts in to narrow.
  const [showCodeSubjectPicker, setShowCodeSubjectPicker] = useState(false);
  // True when we arrived via ?continuePrevious=true with subject + mode
  // set. The selector UI is suppressed and an auto-start effect fires
  // exactly once. Held in state so the placeholder render is stable.
  const [autoStarting, setAutoStarting] = useState(false);

  useEffect(() => {
    getSessionConfig()
      .then((res) => {
        setConfig(res.data);
        // The server decides this now and sends the answer as
        // `outsideActiveHours`. It used to be recomputed here from
        // activeHoursStart/End using getUTCHours(), which only agreed with
        // the server while the server was also using UTC. Once the server
        // moved to the family's real timezone the two drifted an hour apart
        // through BST, so the UI would offer to start a session that the
        // server then refused. One rule, one implementation.
        if (typeof res.data.outsideActiveHours === 'boolean') {
          setOutsideHours(res.data.outsideActiveHours);
        } else {
          // Fallback for a cached client talking to an older server. Uses
          // the DEVICE's local hour, not UTC — wrong for a child abroad,
          // but right for everyone at home, which the UTC version never was.
          const { activeHoursStart, activeHoursEnd } = res.data;
          const alwaysAvailable = activeHoursStart === 0 && activeHoursEnd === 0;
          const hour = new Date().getHours();
          setOutsideHours(!alwaysAvailable && !(hour >= activeHoursStart && hour < activeHoursEnd));
        }

        // Pre-select from URL params (?subject=maths&mode=homework). Lands the
        // child back in the same topic when they arrive via the "Study this
        // topic again" button from /child/history. Subject is validated against
        // the configured list so a stale link can't pre-select something the
        // child no longer has access to; mode is validated against the four
        // known modes so a bad value can't bypass the default.
        const subjectParam = searchParams.get('subject');
        const modeParam    = searchParams.get('mode');
        const validModes = ['homework', 'explore', 'revision', 'code', 'eleven_plus'];
        if (modeParam && validModes.includes(modeParam)) {
          // Don't pre-select 11+ if the child isn't eligible — the tile
          // won't render and the start button would dead-end.
          if (modeParam === 'eleven_plus' && !res.data.elevenPlusAvailable) {
            // skip — leave default mode
          } else {
            setMode(modeParam);
          }
        }
        if (subjectParam) {
          const allowed = (res.data.subjects || []).some((s) => s.key === subjectParam);
          if (allowed) {
            setSubject(subjectParam);
            // Code mode hides the grid by default — expand it so the pre-
            // selected subject is visible rather than silently held in state.
            if (modeParam === 'code') {
              setShowCodeSubjectPicker(true);
            }
          }
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setError('Please log in to use Lantern.');
        } else {
          setError('Could not connect to Lantern. Please try again.');
        }
      });

    // Recent sessions are best-effort: if this fails the page still works,
    // the child just doesn't see the resume option.
    getSessions()
      .then((res) => setRecentSessions(res.data?.sessions || []))
      .catch(() => { /* swallow — resume is non-critical */ });
  }, [searchParams]);

  // Auto-start a fresh session when arriving with ?continuePrevious=true
  // and a valid subject + mode in the URL. Fires exactly once per mount
  // (guarded by autoStarting → starting). The selector UI render path
  // already returns the autoStarting placeholder below so the child never
  // sees the selector flash before navigation. The continuity helper on
  // the server picks up the prior sealed session in the same subject +
  // mode and injects the last few turns into the new chat.
  useEffect(() => {
    if (!config) return;
    if (autoStarting || starting) return;
    if (searchParams.get('continuePrevious') !== 'true') return;

    const subjectParam = searchParams.get('subject');
    const modeParam    = searchParams.get('mode');
    const validModes   = ['homework', 'explore', 'revision', 'code', 'eleven_plus'];
    if (!modeParam || !validModes.includes(modeParam)) return;
    if (modeParam === 'eleven_plus' && !config.elevenPlusAvailable) return;

    // Subject may be omitted for explore/code/eleven_plus, but if present
    // it must be one the child is allowed to pick.
    let subjectToSend = null;
    if (subjectParam) {
      const allowed = (config.subjects || []).some((s) => s.key === subjectParam);
      if (!allowed) return;
      subjectToSend = subjectParam;
    }

    setAutoStarting(true);
    setStarting(true);
    startSession(subjectToSend, modeParam)
      .then((res) => {
        localStorage.setItem('currentSession', JSON.stringify(res.data));
        navigate(`/child/chat/${res.data.sessionId}`);
      })
      .catch((err) => {
        // Soft fail back into the regular selector so the child can
        // pick manually. Surface the server message if there is one.
        const msg = err.response?.data?.error || 'Could not continue your last session. Pick again below.';
        setError(msg);
        setAutoStarting(false);
        setStarting(false);
      });
  }, [config, searchParams, autoStarting, starting, navigate]);

  // Per-mode subject rules:
  //   homework + revision → subject required
  //   explore             → subject optional (grid shown, child can skip)
  //   code                → subject hidden by default; opt-in to narrow
  const subjectRequired = mode === 'homework' || mode === 'revision';
  const showSubjectGrid =
    subjectRequired ||
    mode === 'explore' ||
    (mode === 'code' && showCodeSubjectPicker);

  // What we actually send to the server. The grid may be hidden in code mode
  // even if the child has a subject in state from an earlier mode — don't send
  // it unless the picker is visible.
  const subjectToSend = showSubjectGrid ? subject : null;

  // Top three still-active sessions, newest first — drives the "Pick up where
  // you left off" section above the subject grid. We rely on the server's
  // /api/child/sessions endpoint, which already returns the 10 most recent
  // sessions for this child ordered by started_at desc.
  const pickUpSessions = recentSessions
    .filter((s) => !s.sealed)
    .slice()
    .sort((a, b) => (b.started_at || 0) - (a.started_at || 0))
    .slice(0, 3);

  const hasSealedSessions = recentSessions.some((s) => s.sealed);

  // Most recently sealed session if it ended within the last 2 hours —
  // drives the "Continue your last session" prompt that sits just below
  // "Pick up where you left off". Tapping it routes through
  // SessionHistory's auto-open URL so the child arrives at the detail
  // view with the "Continue chatting" CTA front-and-centre.
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const recentlySealedSession = (() => {
    const sealed = recentSessions
      .filter((s) => s.sealed && s.ended_at && (Date.now() - s.ended_at) < TWO_HOURS_MS)
      .slice()
      .sort((a, b) => (b.ended_at || 0) - (a.ended_at || 0));
    return sealed[0] || null;
  })();

  const handleResumeSession = (sessionId) => {
    if (starting) return;
    navigate(`/child/chat/${sessionId}`);
  };

  const handleContinueLastSealed = (sessionId) => {
    if (starting || autoStarting) return;
    navigate(`/child/history?sessionId=${encodeURIComponent(sessionId)}`);
  };

  const handleStart = async () => {
    if (!mode || starting) return;
    if (subjectRequired && !subject) return;
    setStarting(true);
    setError(null);
    try {
      const res = await startSession(subjectToSend, mode);
      localStorage.setItem('currentSession', JSON.stringify(res.data));
      navigate(`/child/chat/${res.data.sessionId}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not start session. Please try again.';
      setError(msg);
      setStarting(false);
    }
  };

  function handleModeChange(newMode) {
    if (newMode === mode) return;
    setMode(newMode);
    // Switching to code from another mode: collapse the picker by default.
    if (newMode === 'code') setShowCodeSubjectPicker(false);
  }

  const isReady =
    !!mode && !outsideHours && (!subjectRequired || !!subject);

  return (
    <>
      <style>{`
        @keyframes ss-pop { 0%{opacity:0;transform:translateY(8px) scale(0.98);} 100%{opacity:1;transform:translateY(0) scale(1);} }
        @keyframes ss-spin { to { transform: rotate(360deg); } }
        .ss-card { animation: ss-pop 320ms cubic-bezier(0.2,0.8,0.2,1) both; }
        .ss-subject-btn {
          padding: 18px 12px 14px;
          border-radius: 18px;
          border: 2px solid ${C.border};
          background: ${C.card};
          cursor: pointer;
          text-align: center;
          transition: border-color 160ms ease, background 160ms ease, transform 90ms ease, box-shadow 160ms ease;
          outline: none;
          font-family: 'Source Sans 3', sans-serif;
          box-shadow: 0 2px 0 ${C.border};
          -webkit-tap-highlight-color: transparent;
        }
        .ss-subject-btn:hover:not(:disabled) {
          border-color: ${C.accent};
          background: ${C.accentSoft};
        }
        .ss-subject-btn:active {
          transform: translateY(1px);
          box-shadow: 0 1px 0 ${C.border};
        }
        .ss-subject-btn--selected {
          border-color: ${C.accent};
          background: ${C.accentSoft};
          box-shadow: 0 4px 0 ${C.accent}66, 0 0 0 4px ${C.accent}1f;
        }
        .ss-subject-btn:focus-visible {
          box-shadow: 0 2px 0 ${C.border}, 0 0 0 4px ${C.accent}55;
        }
        .ss-mode-btn {
          padding: 14px 16px;
          border-radius: 18px;
          border: 2px solid ${C.border};
          background: ${C.card};
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 14px;
          transition: border-color 160ms ease, background 160ms ease, transform 90ms ease;
          outline: none;
          text-align: left;
          font-family: 'Source Sans 3', sans-serif;
          width: 100%;
          box-shadow: 0 2px 0 ${C.border};
          -webkit-tap-highlight-color: transparent;
        }
        .ss-mode-btn:hover:not(:disabled) {
          border-color: ${C.accent};
          background: ${C.accentSoft};
        }
        .ss-mode-btn:active { transform: translateY(1px); box-shadow: 0 1px 0 ${C.border}; }
        .ss-mode-btn--selected {
          border-color: ${C.accent};
          background: ${C.accentSoft};
          box-shadow: 0 4px 0 ${C.accent}66;
        }
        .ss-mode-btn:focus-visible {
          box-shadow: 0 2px 0 ${C.border}, 0 0 0 4px ${C.accent}55;
        }
        .ss-start {
          width: 100%;
          min-height: 56px;
          padding: 16px 24px;
          background: ${C.accent};
          color: #fff;
          border: none;
          border-radius: 18px;
          font-family: ${HEADING_FONT};
          font-size: 19px;
          font-weight: 800;
          letter-spacing: 0.3px;
          cursor: pointer;
          box-shadow: 0 4px 0 ${C.accentDark}, 0 8px 20px ${C.accent}55;
          transition: transform 90ms ease, background 160ms ease, box-shadow 160ms ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .ss-start:hover:not(:disabled) {
          background: ${C.accentDark};
          box-shadow: 0 4px 0 ${C.accentDark}, 0 10px 24px ${C.accent}66;
        }
        .ss-start:active:not(:disabled) {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${C.accentDark}, 0 4px 10px ${C.accent}55;
        }
        .ss-start:disabled {
          background: #E5E7EB;
          color: #9CA3AF;
          box-shadow: 0 4px 0 #D1D5DB;
          cursor: not-allowed;
        }
        .ss-spinner {
          width: 18px; height: 18px; border-radius: 50%;
          border: 2.5px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          animation: ss-spin 700ms linear infinite;
        }
        .ss-loading-spin {
          width: 36px; height: 36px; border-radius: 50%;
          border: 3px solid ${C.border};
          border-top-color: ${C.accent};
          animation: ss-spin 800ms linear infinite;
        }
        .ss-resume-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 16px;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          box-shadow: 0 2px 0 ${C.border};
          transition: border-color 160ms ease, background 160ms ease;
        }
        .ss-resume-row:hover { border-color: ${C.accent}99; background: #FFFBF0; }
        .ss-resume-cta {
          padding: 9px 16px;
          border-radius: 12px;
          background: ${C.accent};
          color: #fff;
          border: none;
          font-family: ${HEADING_FONT};
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.2px;
          cursor: pointer;
          box-shadow: 0 2px 0 ${C.accentDark};
          transition: background 160ms ease, transform 90ms ease, box-shadow 160ms ease;
          flex-shrink: 0;
        }
        .ss-resume-cta:hover:not(:disabled) {
          background: ${C.accentDark};
          box-shadow: 0 2px 0 ${C.accentDark}, 0 4px 10px ${C.accent}55;
        }
        .ss-resume-cta:active:not(:disabled) {
          transform: translateY(2px);
          box-shadow: 0 0 0 ${C.accentDark};
        }
        .ss-resume-cta:focus-visible {
          outline: none;
          box-shadow: 0 2px 0 ${C.accentDark}, 0 0 0 4px ${C.accent}55;
        }
        .ss-resume-divider {
          height: 1px;
          background: ${C.border};
          margin: 24px 0;
          opacity: 0.6;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, rgba(245,158,11,0.11) 0%, rgba(245,158,11,0) 60%)`,
        // env(safe-area-inset-top) is 0 outside notched/PWA contexts and the
        // real status-bar height inside an iOS standalone PWA — keeps the
        // brand + heading clear of the status bar / camera notch.
        padding: 'calc(28px + env(safe-area-inset-top)) 18px 40px',
        fontFamily: "'Source Sans 3', sans-serif",
        color: C.text,
        boxSizing: 'border-box',
      }}>
        {/* Loading state */}
        {!config && !error && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 480,
            gap: 18,
          }}>
            <LanternMark size={64} />
            <div className="ss-loading-spin" aria-hidden="true" />
            <p style={{ color: C.textMuted, margin: 0, fontSize: 15, fontFamily: HEADING_FONT, fontWeight: 700 }}>
              Lighting things up…
            </p>
          </div>
        )}

        {/* Error-only (no config) */}
        {error && !config && (
          <div className="ss-card" style={{
            padding: '14px 16px',
            background: C.errorBg,
            color: C.errorText,
            borderRadius: 16,
            border: `1.5px solid ${C.errorBorder}`,
            fontWeight: 600,
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 460,
            margin: '40px auto 0',
          }}>
            {error}
          </div>
        )}

        {/* Auto-start placeholder — when we arrived with ?continuePrevious=true
            and a valid subject+mode, the auto-start effect above fires
            startSession() and navigates to chat. Render a minimal loading
            card here so the child never sees the selector flash before
            the redirect. */}
        {config && autoStarting && (
          <div className="ss-card" style={{
            maxWidth: 460,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            gap: 16,
          }}>
            <LanternMark size={56} />
            <div className="ss-loading-spin" aria-hidden="true" />
            <p style={{
              color: C.textMuted,
              margin: 0,
              fontSize: 15,
              fontFamily: HEADING_FONT,
              fontWeight: 700,
            }}>
              Picking up where you left off…
            </p>
          </div>
        )}

        {config && !autoStarting && (
          <div className="ss-card" style={{ maxWidth: 460, margin: '0 auto' }}>
            <InstallPrompt />
            {/* Header */}
            <header style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <LanternMark size={52} />
              </div>
              <h1 style={{
                margin: 0,
                fontFamily: HEADING_FONT,
                fontSize: 26,
                fontWeight: 800,
                color: C.text,
                letterSpacing: '-0.5px',
              }}>
                Hi, {config.displayName}!
              </h1>
              <p style={{ margin: '6px 0 0', fontSize: 15, color: C.textMuted, lineHeight: 1.45 }}>
                Year {config.effectiveYear} — what shall we work on today?
              </p>
            </header>

            {/* Outside hours warning */}
            {outsideHours && (
              <div style={{
                padding: '12px 14px',
                background: C.warnBg,
                color: C.warnText,
                borderRadius: 16,
                border: `1.5px solid ${C.warnBorder}`,
                fontSize: 14,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>🌙</span>
                <span>
                  Lantern is on between <strong>{config.activeHoursStart}:00</strong> and <strong>{config.activeHoursEnd}:00</strong>. Ask your parent or guardian to change your active hours.
                </span>
              </div>
            )}

            {/* In-flow error */}
            {error && (
              <div style={{
                padding: '12px 14px',
                background: C.errorBg,
                color: C.errorText,
                borderRadius: 16,
                border: `1.5px solid ${C.errorBorder}`,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 20,
                textAlign: 'center',
              }}>
                {error}
              </div>
            )}

            {/* Continue your last session — when a sealed session ended
                within the last 2 hours, surface it as a one-tap entry to
                the SessionHistory detail view, where the "Continue
                chatting" CTA is waiting. Sits at the very top of the
                card because it's the most likely thing a returning child
                wants to do. */}
            {recentlySealedSession && (() => {
              const meta = getSubjectMeta(recentlySealedSession.subject, config.subjects);
              const modeLabel = MODE_LABEL[recentlySealedSession.mode] || recentlySealedSession.mode;
              const when = formatRelativeTime(recentlySealedSession.ended_at);
              return (
                <>
                  <section style={{ marginBottom: 24 }}>
                    <p style={{
                      fontFamily: HEADING_FONT,
                      fontSize: 13,
                      fontWeight: 800,
                      color: C.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      margin: '0 0 12px',
                    }}>
                      Continue your last session
                    </p>
                    <div className="ss-resume-row">
                      <span aria-hidden="true" style={{
                        width: 40, height: 40, borderRadius: 12,
                        background: C.accentSoft,
                        border: `1.5px solid ${C.border}`,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 20, flexShrink: 0,
                      }}>
                        {meta.icon}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          margin: 0,
                          fontFamily: HEADING_FONT,
                          fontSize: 15,
                          fontWeight: 800,
                          color: C.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {meta.displayName} · {modeLabel}
                        </p>
                        <p style={{
                          margin: '2px 0 0',
                          fontSize: 12.5,
                          color: C.textMuted,
                          fontWeight: 600,
                        }}>
                          {when}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="ss-resume-cta"
                        onClick={() => handleContinueLastSealed(recentlySealedSession.id)}
                        disabled={starting || outsideHours}
                        aria-label={`Open last session in ${meta.displayName}`}
                      >
                        Open
                      </button>
                    </div>
                  </section>
                  <div className="ss-resume-divider" aria-hidden="true" />
                </>
              );
            })()}

            {/* Pick up where you left off — only shown when the child has
                unsealed sessions to resume. Sits above everything else so
                returning kids see it first without having to re-pick a
                subject and mode. */}
            {pickUpSessions.length > 0 && (
              <>
                <section style={{ marginBottom: 24 }}>
                  <p style={{
                    fontFamily: HEADING_FONT,
                    fontSize: 13,
                    fontWeight: 800,
                    color: C.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    margin: '0 0 12px',
                  }}>
                    Pick up where you left off
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {pickUpSessions.map((s) => {
                      const meta  = getSubjectMeta(s.subject, config.subjects);
                      const when  = formatRelativeTime(s.started_at);
                      const modeLabel = MODE_LABEL[s.mode] || s.mode;
                      return (
                        <div key={s.id} className="ss-resume-row">
                          <span aria-hidden="true" style={{
                            width: 40, height: 40, borderRadius: 12,
                            background: C.accentSoft,
                            border: `1.5px solid ${C.border}`,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20, flexShrink: 0,
                          }}>
                            {meta.icon}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              margin: 0,
                              fontFamily: HEADING_FONT,
                              fontSize: 15,
                              fontWeight: 800,
                              color: C.text,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {meta.displayName}
                            </p>
                            <p style={{
                              margin: '2px 0 0',
                              fontSize: 12.5,
                              color: C.textMuted,
                              fontWeight: 600,
                            }}>
                              {modeLabel} · {when}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="ss-resume-cta"
                            onClick={() => handleResumeSession(s.id)}
                            disabled={starting || outsideHours}
                            aria-label={`Continue ${modeLabel.toLowerCase()} session in ${meta.displayName}`}
                          >
                            Continue
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
                <div className="ss-resume-divider" aria-hidden="true" />
              </>
            )}

            {/* Subject picker — visible when required (homework/revision),
                always for explore (optional), and for code only after the
                child opts in via the disclosure below. */}
            {showSubjectGrid && (
              <section style={{ marginBottom: 24 }}>
                <p style={{
                  fontFamily: HEADING_FONT,
                  fontSize: 13,
                  fontWeight: 800,
                  color: C.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  margin: '0 0 12px',
                }}>
                  {subjectRequired ? 'Choose a subject' : 'Choose a subject (optional)'}
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10,
                }}>
                  {config.subjects.map((s) => {
                    const sel = subject === s.key;
                    return (
                      <button
                        key={s.key}
                        className={`ss-subject-btn ${sel ? 'ss-subject-btn--selected' : ''}`}
                        onClick={() => setSubject(sel ? null : s.key)}
                        aria-pressed={sel}
                      >
                        <span aria-hidden="true" style={{ fontSize: 30, display: 'block', marginBottom: 4, lineHeight: 1 }}>{s.icon}</span>
                        <span style={{
                          fontFamily: HEADING_FONT,
                          fontSize: 13,
                          fontWeight: 700,
                          color: sel ? C.accentDark : C.text,
                        }}>
                          {s.displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Lantern-remembers indicator */}
                {subject && config.subjectsWithContext && config.subjectsWithContext.includes(subject) && (
                  <div style={{
                    marginTop: 12,
                    padding: '10px 14px',
                    background: C.greenSoft,
                    border: `1.5px solid ${C.greenBorder}`,
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13.5,
                    color: C.greenInk,
                    fontWeight: 600,
                    animation: 'ss-pop 240ms cubic-bezier(0.2,0.8,0.2,1) both',
                  }}>
                    <span aria-hidden="true" style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: C.green, color: '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, flexShrink: 0,
                    }}>✓</span>
                    <span>
                      I remember where you got to in <strong>{config.subjects.find(s2 => s2.key === subject)?.displayName || subject}</strong> — let's pick up there!
                    </span>
                  </div>
                )}
              </section>
            )}

            {/* Code-mode disclosure — replaces the subject grid slot when
                in code mode and the child hasn't opted to narrow. */}
            {mode === 'code' && !showCodeSubjectPicker && (
              <section style={{ marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={() => setShowCodeSubjectPicker(true)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: `2px dashed ${C.border}`,
                    background: 'transparent',
                    borderRadius: 16,
                    color: C.textMuted,
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'border-color 160ms ease, color 160ms ease, background 160ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.color = C.accentDark;
                    e.currentTarget.style.background = C.accentSoft;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.color = C.textMuted;
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  💡 Want to focus on a specific subject? <span style={{ color: C.accent, marginLeft: 4 }}>Pick one</span>
                </button>
              </section>
            )}

            {/* My past sessions — sits between subject and mode pickers when
                the child has at least one sealed session to look back at. */}
            {hasSealedSessions && (
              <div style={{ textAlign: 'center', margin: '0 0 22px' }}>
                <button
                  type="button"
                  onClick={() => navigate('/child/history')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 6px',
                    fontFamily: HEADING_FONT,
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.textMuted,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    letterSpacing: '0.02em',
                    transition: 'color 160ms ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = C.accentDark; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = C.textMuted; }}
                >
                  <span aria-hidden="true">📚</span>
                  My past sessions
                  <span aria-hidden="true" style={{ marginLeft: 2 }}>›</span>
                </button>
              </div>
            )}

            {/* Mode picker */}
            <section style={{ marginBottom: 24 }}>
              <p style={{
                fontFamily: HEADING_FONT,
                fontSize: 13,
                fontWeight: 800,
                color: C.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                margin: '0 0 12px',
              }}>
                What do you want to do?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {buildSubjectModes(config.effectiveYear, config.elevenPlusAvailable).map((m) => {
                  const sel = mode === m.key;
                  return (
                    <button
                      key={m.key}
                      className={`ss-mode-btn ${sel ? 'ss-mode-btn--selected' : ''}`}
                      onClick={() => handleModeChange(m.key)}
                      aria-pressed={sel}
                    >
                      <span aria-hidden="true" style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: sel ? C.card : C.bg,
                        border: `1.5px solid ${sel ? C.accent : C.border}`,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, flexShrink: 0,
                      }}>
                        {m.icon}
                      </span>
                      <span style={{ flex: 1 }}>
                        <p style={{
                          margin: 0,
                          fontFamily: HEADING_FONT,
                          fontSize: 15,
                          fontWeight: 800,
                          color: sel ? C.accentDark : C.text,
                        }}>{m.label}</p>
                        <p style={{
                          margin: '2px 0 0',
                          fontSize: 13,
                          color: C.textMuted,
                          lineHeight: 1.35,
                        }}>{m.description}</p>
                      </span>
                      {sel && (
                        <span aria-hidden="true" style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: C.accent, color: '#fff',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 800, flexShrink: 0,
                        }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Start button */}
            <button
              className="ss-start"
              onClick={handleStart}
              disabled={!isReady || starting}
            >
              {starting ? (
                <>
                  <span className="ss-spinner" aria-hidden="true" />
                  <span>Starting…</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                    <path d="M5 2 L14 9 L5 16 Z" fill="#fff" />
                  </svg>
                  <span>Start session</span>
                </>
              )}
            </button>

            {/* Footer info */}
            <p style={{
              fontSize: 13,
              color: C.textMuted,
              textAlign: 'center',
              margin: '16px 0 0',
              display: 'flex',
              justifyContent: 'center',
              gap: 14,
              flexWrap: 'wrap',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span aria-hidden="true">⏱</span> {config.timeLimit} minute limit
              </span>
              {config.voiceInputEnabled && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span aria-hidden="true">🎙</span> Voice on
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
